import React, { Suspense, useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { Float, Stars, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import { TextureLoader } from 'three';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';

// ─── DATA ──────────────────────────────────────────────────────────
const PROJECTS = [
  {
    title: 'fair-technology',
    subtitle: 'Technologie für kommunale Infrastruktur',
    tag: 'WEB · BRANDING · TECH',
    href: 'https://fair-technology.de',
    img: `${process.env.PUBLIC_URL}/images/fair-tec.jpeg`,
  },
  {
    title: 'Samusew',
    subtitle: 'Gebäude- & Garten-Management · Rhein-Neckar',
    tag: 'WEB · BRAND · SEO',
    href: 'https://samusew-clean-life.de',
    img: `${process.env.PUBLIC_URL}/images/samusew.jpeg`,
  },
  {
    title: 'ONE-A-COMMUNICATION',
    subtitle: 'Strategische Kommunikationsberatung',
    tag: 'WEB · UX · STRATEGY',
    href: 'https://one-a-communication.de',
    img: `${process.env.PUBLIC_URL}/images/one-a-com.jpeg`,
  },
  {
    title: 'DAD-Service',
    subtitle: 'Elektromeister & Gebäudetechnik · Rhein-Main',
    tag: 'WEB · UX · CMS',
    href: 'https://dad-service.de',
    img: `${process.env.PUBLIC_URL}/images/dad-service.jpeg`,
  },
];

// Hero shots are ~16:9 — match the screen plane so nothing stretches
const SCREEN_W = 3.4;
const SCREEN_H = 3.4 * (9 / 16); // 1.9125
const BASE_DEPTH = 2.3;
const ACCENT = '#60A5FA'; // brighter blue — readable on pure black

// ─── REDUCED MOTION ────────────────────────────────────────────────
function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

// ─── PAGE-SCROLL PROGRESS (0..1 over the section) ─────────────────
function usePageScrollProgress(ref) {
  const progressRef = useRef(0);
  useEffect(() => {
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      if (total <= 0) {
        progressRef.current = 0;
        return;
      }
      const scrolled = -rect.top;
      progressRef.current = Math.min(1, Math.max(0, scrolled / total));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ref]);
  return progressRef;
}

// ─── MACBOOK ───────────────────────────────────────────────────────
function MacBook({ position, rotation, imgUrl, hoverPhase = 0, onClick, focused, reduced, isMobile }) {
  const tex = useLoader(TextureLoader, imgUrl);
  tex.anisotropy = 16;
  tex.colorSpace = THREE.SRGBColorSpace;

  const group = useRef();
  const lid = useRef();

  // Lid open-angle: 0 = upright, slight backward lean like an open MacBook
  const LID_OPEN = -0.18;

  useFrame((state) => {
    if (reduced) return;
    const t = state.clock.elapsedTime;
    if (group.current) {
      group.current.position.y = position[1] + Math.sin(t * 0.6 + hoverPhase) * 0.08;
    }
    if (lid.current) {
      lid.current.rotation.x = LID_OPEN + Math.sin(t * 0.4 + hoverPhase) * 0.015;
    }
  });

  return (
    <group ref={group} position={position} rotation={rotation} onClick={onClick}>
      {/* ── BASE (rounded slab) */}
      <RoundedBox
        args={[SCREEN_W + 0.14, 0.1, BASE_DEPTH]}
        radius={0.04}
        smoothness={4}
        position={[0, -0.05, 0]}
        castShadow
        receiveShadow
      >
        <meshStandardMaterial color="#c8c8cc" metalness={0.85} roughness={0.28} />
      </RoundedBox>

      {/* Keyboard well (slightly recessed dark area) */}
      <mesh position={[0, 0.001, -0.15]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[SCREEN_W - 0.1, BASE_DEPTH - 1.2]} />
        <meshStandardMaterial color="#16161a" roughness={0.85} metalness={0.1} />
      </mesh>
      {/* Keyboard keys hint — desktop only (84 planes; skipped on mobile for perf) */}
      {!isMobile &&
        Array.from({ length: 6 }).map((_, row) =>
          Array.from({ length: 14 }).map((_, col) => (
            <mesh
              key={`${row}-${col}`}
              position={[-1.45 + col * 0.22, 0.005, -0.55 + row * 0.13]}
              rotation={[-Math.PI / 2, 0, 0]}
            >
              <planeGeometry args={[0.18, 0.105]} />
              <meshStandardMaterial color="#2a2a2e" roughness={0.7} />
            </mesh>
          ))
        )}
      {/* Trackpad */}
      <mesh position={[0, 0.006, 0.55]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[1.25, 0.78]} />
        <meshStandardMaterial color="#9c9ca0" metalness={0.6} roughness={0.45} />
      </mesh>

      {/* Hinge — thin cylinder behind base */}
      <mesh position={[0, 0.0, -BASE_DEPTH / 2 + 0.02]} rotation={[0, 0, Math.PI / 2]}>
        <cylinderGeometry args={[0.04, 0.04, SCREEN_W + 0.06, 16]} />
        <meshStandardMaterial color="#48484c" metalness={0.9} roughness={0.4} />
      </mesh>

      {/* ── LID — pivot from rear edge of base */}
      <group position={[0, 0, -BASE_DEPTH / 2]}>
        <group ref={lid} rotation={[-0.18, 0, 0]}>
          {/* Lid back panel (rounded, aluminium) */}
          <RoundedBox
            args={[SCREEN_W + 0.08, SCREEN_H + 0.08, 0.06]}
            radius={0.04}
            smoothness={4}
            position={[0, SCREEN_H / 2, -0.03]}
            castShadow
          >
            <meshStandardMaterial color="#c8c8cc" metalness={0.85} roughness={0.28} />
          </RoundedBox>
          {/* Apple-style logo placeholder — a subtle white roundel */}
          <mesh position={[0, SCREEN_H / 2, -0.061]}>
            <circleGeometry args={[0.13, 24]} />
            <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={0.4} />
          </mesh>
          {/* Bezel (matte black surround) */}
          <mesh position={[0, SCREEN_H / 2, 0.001]}>
            <planeGeometry args={[SCREEN_W + 0.02, SCREEN_H + 0.02]} />
            <meshStandardMaterial color="#0a0a0a" roughness={0.95} />
          </mesh>
          {/* SCREEN — texture, fills bezel cleanly (matched 2:1 aspect) */}
          <mesh position={[0, SCREEN_H / 2, 0.004]}>
            <planeGeometry args={[SCREEN_W - 0.06, SCREEN_H - 0.06]} />
            <meshBasicMaterial map={tex} toneMapped={false} />
          </mesh>
          {/* Camera notch */}
          <mesh position={[0, SCREEN_H - 0.03, 0.005]}>
            <circleGeometry args={[0.018, 16]} />
            <meshBasicMaterial color="#000" />
          </mesh>
        </group>
      </group>

      {/* Screen glow lighting surroundings */}
      <pointLight position={[0, 1.1, 0.5]} color="#ffffff" intensity={focused ? 2.5 : 1.5} distance={10} decay={2} />
    </group>
  );
}

// ─── SCENE ─────────────────────────────────────────────────────────
function Scene({ progressRef, reduced, focusIdx, setFocusIdx, isMobile }) {
  // MOBILE: laptops centered, camera flies straight through
  // DESKTOP: alternating left/right corridor with an S-curve camera
  const items = useMemo(() => {
    return PROJECTS.map((p, i) => ({
      ...p,
      key: `${p.title}-${i}`,
      position: isMobile
        ? [0, 0, -i * 6 - 2]
        : [i % 2 === 0 ? -1.8 : 1.8, 0, -i * 7 - 2],
      rotation: isMobile ? [0, 0, 0] : [0, i % 2 === 0 ? 0.42 : -0.42, 0],
      hoverPhase: i * 1.6,
    }));
  }, [isMobile]);

  // Camera curve
  const curve = useMemo(() => {
    const spacing = isMobile ? 6 : 7;
    const totalZ = (items.length - 1) * spacing + 10;
    const pts = [];
    for (let i = 0; i <= 40; i++) {
      const t = i / 40;
      const z = 4 - t * totalZ;
      // Mobile: stay on axis, very gentle y bob
      // Desktop: S-curve weaving between left/right laptops
      const x = isMobile ? 0 : Math.sin(t * Math.PI * 1.5) * 1.4;
      const y = isMobile ? 0.7 + Math.sin(t * Math.PI) * 0.1 : 0.9 + Math.sin(t * Math.PI) * 0.35;
      pts.push(new THREE.Vector3(x, y, z));
    }
    return new THREE.CatmullRomCurve3(pts);
  }, [items.length, isMobile]);

  // Smoothed progress + look-target to kill camera shake
  const smoothed = useRef({ p: 0, look: new THREE.Vector3(0, 0.5, -3) });

  useFrame((state, delta) => {
    const target = reduced ? 0 : progressRef.current;
    // Critically damped follow on scroll progress (no jitter)
    const k = 1 - Math.exp(-delta * 5); // ~5x per second
    smoothed.current.p += (target - smoothed.current.p) * k;
    const p = smoothed.current.p;

    const pos = curve.getPointAt(p);
    const look = curve.getPointAt(Math.min(p + 0.06, 1));
    state.camera.position.copy(pos);

    const lookTarget = new THREE.Vector3(look.x * 0.3, look.y - 0.2, look.z - 3);
    smoothed.current.look.lerp(lookTarget, k);
    state.camera.lookAt(smoothed.current.look);

    let nearest = 0;
    let best = Infinity;
    for (let i = 0; i < items.length; i++) {
      const dz = Math.abs(state.camera.position.z - items[i].position[2]);
      if (dz < best) {
        best = dz;
        nearest = i;
      }
    }
    if (nearest !== focusIdx) setFocusIdx(nearest);
  });

  return (
    <>
      <color attach="background" args={['#000000']} />
      <fog attach="fog" args={['#000000', 8, 30]} />

      {/* LIGHTING — bright enough to see */}
      <ambientLight intensity={0.6} />
      <directionalLight position={[6, 9, 4]} intensity={1.4} color="#ffffff" castShadow />
      <directionalLight position={[-4, 4, -8]} intensity={1.0} color="#ffffff" />
      <pointLight position={[0, 3, -12]} intensity={2.0} color={ACCENT} distance={25} decay={2} />
      <pointLight position={[5, 3, 2]} intensity={1.0} color="#ffffff" distance={15} />

      {!reduced && !isMobile && <Stars radius={80} depth={60} count={1500} factor={3} saturation={0} fade speed={0.5} />}

      {/* Simple dark floor (no mirror — too heavy) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.5, -12]} receiveShadow>
        <planeGeometry args={[80, 100]} />
        <meshStandardMaterial color="#0a0a0a" metalness={0.4} roughness={0.7} />
      </mesh>
      <gridHelper args={[80, 40, '#2a2a2a', '#141414']} position={[0, -1.49, -12]} />

      {items.map((it, i) => (
        <group key={it.key}>
          <Float speed={reduced ? 0 : 0.6} rotationIntensity={reduced ? 0 : 0.04} floatIntensity={reduced ? 0 : 0.12}>
            <MacBook
              position={it.position}
              rotation={it.rotation}
              imgUrl={it.img}
              hoverPhase={it.hoverPhase}
              focused={focusIdx === i}
              reduced={reduced}
              isMobile={isMobile}
              onClick={() => window.open(it.href, '_blank', 'noopener,noreferrer')}
            />
          </Float>
        </group>
      ))}
    </>
  );
}

// ─── PUBLIC ────────────────────────────────────────────────────────
export default function Gallery3D() {
  const reduced = useReducedMotion();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [focusIdx, setFocusIdx] = useState(0);
  const sectionRef = useRef(null);
  const progressRef = usePageScrollProgress(sectionRef);
  const totalStations = PROJECTS.length;
  const progressDisplay = totalStations > 1 ? focusIdx / (totalStations - 1) : 0;

  return (
    // OUTER SECTION: 300vh tall — controls scroll length of the flythrough
    <Box
      ref={sectionRef}
      sx={{
        position: 'relative',
        zIndex: 2,
        width: '100%',
        height: { xs: '200vh', md: '350vh' },
        bgcolor: 'black',
      }}
    >
      {/* INNER STICKY: 100vh canvas pinned while outer scrolls */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          height: '100vh',
          width: '100%',
          overflow: 'hidden',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Eyebrow */}
        <Box sx={{ position: 'absolute', top: 28, left: { xs: 16, md: 40 }, zIndex: 3, pointerEvents: 'none' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.35em', textTransform: 'uppercase', fontFamily: 'Montserrat, sans-serif' }}>
            Werkschau
          </Typography>
          <Typography sx={{ color: 'white', fontSize: { xs: '1.4rem', md: '2.2rem' }, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1, mt: 1, fontFamily: 'Montserrat, sans-serif' }}>
            Projekt-<br />Galerie
          </Typography>
        </Box>

        <Box sx={{ position: 'absolute', top: 32, right: { xs: 16, md: 40 }, zIndex: 3, pointerEvents: 'none', textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', fontFamily: 'monospace' }}>
            Scroll ↓ to fly through
          </Typography>
        </Box>

        {/* Focused-project HUD caption — 2D, clean, no 3D occlusion artifacts */}
        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 90, md: 110 },
            left: { xs: 16, md: 40 },
            right: { xs: 16, md: 40 },
            zIndex: 3,
            pointerEvents: 'none',
            textAlign: { xs: 'left', md: 'left' },
          }}
        >
          {PROJECTS.map((p, i) => (
            <Box
              key={p.title}
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                opacity: focusIdx === i ? 1 : 0,
                transform: focusIdx === i ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 500ms ease-out, transform 500ms ease-out',
              }}
            >
              <Typography
                sx={{
                  color: ACCENT,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  fontFamily: 'monospace',
                  mb: 1,
                }}
              >
                {p.tag}
              </Typography>
              <Typography
                sx={{
                  color: 'white',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 900,
                  fontSize: { xs: '1.6rem', md: '2.6rem' },
                  letterSpacing: '-0.03em',
                  lineHeight: 1.05,
                }}
              >
                {p.title}
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: { xs: '0.85rem', md: '0.95rem' },
                  mt: 0.8,
                  fontFamily: 'Montserrat, sans-serif',
                }}
              >
                {p.subtitle}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Progress bar */}
        <Box sx={{ position: 'absolute', bottom: 32, left: { xs: 16, md: 40 }, right: { xs: 16, md: 40 }, zIndex: 3, pointerEvents: 'none' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem', letterSpacing: '0.3em', fontFamily: 'monospace', fontWeight: 700 }}>
              {String(focusIdx + 1).padStart(2, '0')} / {String(totalStations).padStart(2, '0')}
            </Typography>
            <Typography sx={{ color: ACCENT, fontSize: '0.6rem', letterSpacing: '0.3em', fontFamily: 'monospace', fontWeight: 700 }}>
              {Math.round(progressDisplay * 100)}%
            </Typography>
          </Box>
          <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.1)', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', inset: 0, width: `${progressDisplay * 100}%`, bgcolor: 'white', transition: 'width 350ms ease-out' }} />
          </Box>
        </Box>

        <Canvas
          dpr={isMobile ? [1, 1.4] : [1, 2]}
          camera={{ position: [0, 0.9, 4], fov: isMobile ? 55 : 45, near: 0.1, far: 100 }}
          gl={{ antialias: true, alpha: false, powerPreference: 'high-performance' }}
          shadows={!isMobile}
          style={{ position: 'absolute', inset: 0 }}
        >
          <Suspense fallback={null}>
            <Scene progressRef={progressRef} reduced={reduced} focusIdx={focusIdx} setFocusIdx={setFocusIdx} isMobile={isMobile} />
          </Suspense>
        </Canvas>

        {/* Vignette */}
        <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.7) 100%)' }} />
        {/* Top/bottom feather */}
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 80, background: 'linear-gradient(to bottom, #000 0%, transparent 100%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, background: 'linear-gradient(to top, #000 0%, transparent 100%)', pointerEvents: 'none' }} />
      </Box>
    </Box>
  );
}
